// Where each setup step is done (spec/13 §13.1, F-HQ-03): one action per step, a precise link into the screen and the
// row that changes it, or a dialog opened right here. The server decides whether a step is done; this file only knows
// where to send the owner to do it.

import type { OrganizationSetupStepId, VenueSetupStepId } from '@clavian/api-client';

/** A dialog the Setup pages open in place. */
export type SetupDialog = 'addStation' | 'inviteStaff' | 'createVenue' | 'businessDetails' | 'howPlayersPay';

export type StepAction =
  | {
      readonly kind: 'link';
      readonly to: string;
      readonly params: Readonly<Record<string, string>>;
      /** The settings row or panel to reveal (`settings/reveal.ts`), or a page's anchor. */
      readonly hash?: string;
    }
  | { readonly kind: 'dialog'; readonly dialog: SetupDialog };

const settings = (venueSlug: string, group: string, hash?: string): StepAction => ({
  kind: 'link',
  to: '/v/$venueSlug/settings/$group',
  params: { venueSlug, group },
  ...(hash === undefined ? {} : { hash }),
});

const page = (to: string, params: Readonly<Record<string, string>>): StepAction => ({ kind: 'link', to, params });

export interface VenueStepContext {
  /** A venue with stations already pairs one from its Stations page; one with none adds and pairs its first here. */
  readonly hasStations: boolean;
  /** `stations:manage`: the Add station dialog is offered, else the Stations page, which says what the role may do. */
  readonly canAddStation: boolean;
  /** `members:invite`: the invitation dialog is offered, else the Staff page. */
  readonly canInvite: boolean;
}

/** A venue step's action. */
export function venueStepAction(step: VenueSetupStepId, venueSlug: string, context: VenueStepContext): StepAction {
  switch (step) {
    case 'openingHours':
      return settings(venueSlug, 'venue', 'openingHours');
    // How players pay (ADR-0058): the three ways, and the first pass, in a dialog right here.
    case 'price':
      return { kind: 'dialog', dialog: 'howPlayersPay' };
    case 'stationPaired':
      return context.hasStations || !context.canAddStation ? page('/v/$venueSlug/stations', { venueSlug }) : { kind: 'dialog', dialog: 'addStation' };
    case 'taxMode':
      return settings(venueSlug, 'money', 'panel.taxRegistration');
    case 'lockdownTest':
      return page('/v/$venueSlug', { venueSlug });
    case 'printer':
      return settings(venueSlug, 'hardware', 'panel.printers');
    case 'payments':
      return settings(venueSlug, 'money', 'payments.counterUpiVpa');
    case 'kioskSignUp':
      return settings(venueSlug, 'playersAndSignIn', 'kiosk.allowSignUp');
    case 'offlineCodes':
      return page('/v/$venueSlug/sessions/offline', { venueSlug });
    case 'staffPin':
      return page('/v/$venueSlug/account', { venueSlug });
    case 'staffInvited':
      return context.canInvite ? { kind: 'dialog', dialog: 'inviteStaff' } : page('/v/$venueSlug/staff', { venueSlug });
    case 'firstSession':
      return page('/v/$venueSlug', { venueSlug });
  }
}

/** An organisation step's action: both open a dialog on the organisation's Setup page. */
export function organizationStepAction(step: OrganizationSetupStepId): StepAction {
  return { kind: 'dialog', dialog: step === 'firstVenue' ? 'createVenue' : 'businessDetails' };
}
